/**
 * RichText renderer for article block text.
 * Supports four inline formats — chosen to be safe and unambiguous:
 *   **bold**          →  <strong>
 *   *italic*          →  <em>
 *   ++underline++     →  <u>
 *   [text](url)       →  <a href="url" target="_blank">
 *
 * Formats can nest in either order (e.g. **bold *italic***).
 * Newlines are preserved with whitespace-pre-wrap on the parent.
 */
import React from "react";

// Allow http(s), mailto:, tel:, and relative URLs (/something). Block javascript:, data:, etc.
const safeHref = (raw) => {
  const u = (raw || "").trim();
  if (!u) return null;
  if (/^(https?:|mailto:|tel:)/i.test(u)) return u;
  if (u.startsWith("/")) return u;
  if (/^[a-z][a-z0-9+.-]*:/i.test(u)) return null; // unknown scheme — block
  return `https://${u}`;
};

// Process the more-specific markers first so we don't eat their delimiters.
// Link must be FIRST so `[text](url)` isn't gobbled by `*italic*` matching the brackets/parens.
const RULES = [
  { re: /\[([^\]\n]+?)\]\(([^)\s]+)\)/, kind: "link" },
  { re: /\*\*([^*]+?)\*\*/, tag: "strong" },
  { re: /\+\+([^+]+?)\+\+/, tag: "u" },
  { re: /\*([^*]+?)\*/, tag: "em" },
];

const parse = (text, keyPrefix = "rt") => {
  if (!text) return null;
  // Find the earliest match across rules.
  let earliest = null;
  for (const rule of RULES) {
    const m = rule.re.exec(text);
    if (m && (earliest == null || m.index < earliest.match.index)) {
      earliest = { rule, match: m };
    }
  }
  if (!earliest) return text;
  const { rule, match } = earliest;
  const before = text.slice(0, match.index);
  const after = text.slice(match.index + match[0].length);
  const key = `${keyPrefix}-${match.index}`;

  let renderedMatch;
  if (rule.kind === "link") {
    const label = match[1];
    const href = safeHref(match[2]);
    if (href) {
      const internal = href.startsWith("/");
      renderedMatch = (
        <a
          key={key}
          href={href}
          target={internal ? undefined : "_blank"}
          rel={internal ? undefined : "noopener noreferrer"}
          className="text-titans-blue hover:text-titans-red underline-offset-2 hover:underline"
        >
          {parse(label, `${key}-c`)}
        </a>
      );
    } else {
      // Invalid URL → render the label as plain text.
      renderedMatch = parse(label, `${key}-c`);
    }
  } else {
    const Tag = rule.tag;
    renderedMatch = <Tag key={key}>{parse(match[1], `${key}-c`)}</Tag>;
  }

  return [before, renderedMatch, parse(after, `${key}-a`)];
};

export default function RichText({ text, className = "" }) {
  if (!text) return null;
  return <span className={className}>{parse(text)}</span>;
}
